// ------------------------------------------------------------
// ReviewStep.tsx – full summary
// ------------------------------------------------------------
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  Chip,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import { fetchTags } from "./TagService";      // ⬅️ same helper you use in FamilyStep

type Tag = { tag_id: number; tag_name: string; type: string };

type Props = { data: any; onBack: () => void; onFinish: () => void; saving?: boolean };

const ReviewStep: React.FC<Props> = ({ data, onBack, onFinish, saving = false }) => {
  const [tagMap, setTagMap] = useState<Record<number, Tag>>({});

  // build a lookup table { 3: {tag_id:3, tag_name:'pescatarian', …}, … }
  useEffect(() => {
    const loadTags = async () => {
      const allTypes = await Promise.all([
        fetchTags("restriction"),
        fetchTags("medical"),
        fetchTags("allergy"),
      ]);
      const flat: Tag[] = allTypes.flat();
      setTagMap(Object.fromEntries(flat.map((t) => [t.tag_id, t])));
    };
    loadTags();
  }, []);

  const tagName = (id: number) => tagMap[id]?.tag_name ?? "(unknown)";

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Review your family members
      </Typography>

      <List>
        {data.members.map((m: any, idx: number) => (
          <React.Fragment key={idx}>
            <ListItem disableGutters>
              <Box sx={{ width: "100%" }}>
                <Typography variant="h6">{m.name}</Typography>

                {/* Restrictions */}
                <Typography sx={{ mt: 1 }}>
                  <b>Restrictions:</b>{" "}
                  {m.restrictionTagIds?.length ? (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {m.restrictionTagIds.map((id: number) => (
                        <Chip key={id} label={tagName(id)} size="small" />
                      ))}
                    </Stack>
                  ) : (
                    "None"
                  )}
                </Typography>

                {/* Medical */}
                <Typography sx={{ mt: 1 }}>
                  <b>Medical needs:</b>{" "}
                  {m.medicalTagIds.length ? (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {m.medicalTagIds.map((id: number) => (
                        <Chip key={id} label={tagName(id)} size="small" />
                      ))}
                    </Stack>
                  ) : (
                    "None"
                  )}
                </Typography>

                {/* Allergies */}
                <Typography sx={{ mt: 1 }}>
                  <b>Allergies:</b>{" "}
                  {m.allergyTagIds.length ? (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {m.allergyTagIds
                        .filter((id: number | string) => id !== "other")
                        .map((id: number) => (
                          <Chip key={id} label={tagName(id)} size="small" />
                        ))}
                      {m.allergyTagIds.includes("other") &&
                        m.otherAllergyText && (
                          <Chip
                            label={m.otherAllergyText}
                            size="small"
                            variant="outlined"
                          />
                        )}
                    </Stack>
                  ) : (
                    "None"
                  )}
                </Typography>

                {/* Meal Schedule */}
                <Typography><b>Breakfast:</b> {m.mealSchedule.breakfast.join(", ") || "None"}</Typography>
                <Typography><b>Lunch:</b> {m.mealSchedule.lunch.join(", ") || "None"}</Typography>
                <Typography><b>Dinner:</b> {m.mealSchedule.dinner.join(", ") || "None"}</Typography>

              </Box>
            </ListItem>
            {idx < data.members.length - 1 && <Divider sx={{ my: 2 }} />}
          </React.Fragment>
        ))}
      </List>

      <Box
        sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
      >
        <Button onClick={onBack} disabled={saving}>Back</Button>
        <Button variant="contained" onClick={onFinish} disabled={saving}>
          {saving ? <CircularProgress size={24} color="inherit" /> : "Finish"}
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewStep;
